import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { PRODUCT_REFERENCE } from '@/data/productReference';
import {
  Loader2, Upload, Sparkles, Download, Copy, MessageCircle, Mail,
  Facebook, Linkedin, Image as ImageIcon, Wand2, RefreshCw, Save, Trash2,
} from 'lucide-react';

const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Arabic', 'Swedish', 'Italian', 'Portuguese', 'Dutch', 'Chinese', 'Japanese'];
type HashtagSet = { id: string; name: string; language: string; tags: string[]; createdAt: number };
const STORAGE_KEY = 'aureum_hashtag_sets_v1';

type Platform = 'instagram' | 'facebook' | 'linkedin' | 'whatsapp' | 'email';

const PRODUCT_OPTIONS = Object.values(PRODUCT_REFERENCE).map((p) => ({
  key: p.avtName,
  name: p.avtSeries,
  tagline: p.tagline,
  features: p.keyFeatures,
  image: p.images.main as string,
}));

async function urlToDataUrl(url: string): Promise<string> {
  const r = await fetch(url);
  const blob = await r.blob();
  return await new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result as string);
    reader.onerror = rej;
    reader.readAsDataURL(blob);
  });
}

export const SocialStudio = () => {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [productKey, setProductKey] = useState<string>(PRODUCT_OPTIONS[0]?.key || '');
  const [sourceImage, setSourceImage] = useState<string>(PRODUCT_OPTIONS[0]?.image || '');
  const [platform, setPlatform] = useState<Platform>('instagram');
  const [tone, setTone] = useState('elegant, confident, luxurious');
  const [language, setLanguage] = useState('English');
  const [customNotes, setCustomNotes] = useState('');

  const [generatedImage, setGeneratedImage] = useState<string>('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagLanguage, setHashtagLanguage] = useState('English');
  const [savedSets, setSavedSets] = useState<Record<string, HashtagSet[]>>({});

  const [genImgLoading, setGenImgLoading] = useState(false);
  const [genCapLoading, setGenCapLoading] = useState(false);
  const [genTagLoading, setGenTagLoading] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSavedSets(JSON.parse(raw));
    } catch {}
  }, []);
  const persistSets = (next: Record<string, HashtagSet[]>) => {
    setSavedSets(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };
  const currentSets = savedSets[productKey] || [];
  const saveCurrentSet = () => {
    if (!hashtags.length) { toast.error('No hashtags to save'); return; }
    const name = window.prompt('Name this hashtag set', `${hashtagLanguage} – ${new Date().toLocaleDateString()}`);
    if (!name) return;
    const set: HashtagSet = { id: crypto.randomUUID(), name, language: hashtagLanguage, tags: hashtags, createdAt: Date.now() };
    const next = { ...savedSets, [productKey]: [set, ...currentSets] };
    persistSets(next);
    toast.success('Hashtag set saved');
  };
  const loadSet = (id: string) => {
    const s = currentSets.find((x) => x.id === id);
    if (!s) return;
    setHashtags(s.tags);
    setHashtagLanguage(s.language);
    toast.success(`Loaded "${s.name}"`);
  };
  const deleteSet = (id: string) => {
    const next = { ...savedSets, [productKey]: currentSets.filter((x) => x.id !== id) };
    persistSets(next);
  };

  const product = PRODUCT_OPTIONS.find((p) => p.key === productKey);

  const handleProductPick = (key: string) => {
    setProductKey(key);
    const p = PRODUCT_OPTIONS.find((x) => x.key === key);
    if (p) setSourceImage(p.image);
    setGeneratedImage('');
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large (max 10MB)');
      return;
    }
    const path = `social/${user.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const { error } = await supabase.storage.from('product-images').upload(path, file);
    if (error) {
      toast.error('Upload failed: ' + error.message);
      return;
    }
    const { data } = supabase.storage.from('product-images').getPublicUrl(path);
    setSourceImage(data.publicUrl);
    setGeneratedImage('');
    toast.success('Image uploaded');
  };

  const generateImage = async () => {
    if (!sourceImage || !product) return;
    setGenImgLoading(true);
    try {
      // Convert the image to a data URL so the AI can ingest local /assets imports too
      const imageUrl = sourceImage.startsWith('http') ? sourceImage : await urlToDataUrl(sourceImage);
      const { data, error } = await supabase.functions.invoke('social-template-generator', {
        body: {
          mode: 'image',
          productName: product.name,
          tagline: product.tagline,
          platform,
          customPrompt: customNotes,
          imageUrl,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setGeneratedImage(data.imageUrl);
      toast.success('Template image generated');
    } catch (e: any) {
      toast.error(e.message || 'Image generation failed');
    } finally {
      setGenImgLoading(false);
    }
  };

  const generateCaption = async () => {
    if (!product) return;
    setGenCapLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-template-generator', {
        body: {
          mode: 'caption',
          productName: product.name,
          tagline: product.tagline,
          keyFeatures: product.features,
          platform,
          tone,
          language,
          customPrompt: customNotes,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCaption(data.caption || '');
      setHashtags(data.hashtags || []);
      toast.success('Caption generated');
    } catch (e: any) {
      toast.error(e.message || 'Caption generation failed');
    } finally {
      setGenCapLoading(false);
    }
  };
  const generateHashtags = async () => {
    if (!product) return;
    setGenTagLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('social-template-generator', {
        body: {
          mode: 'hashtags',
          productName: product.name,
          tagline: product.tagline,
          keyFeatures: product.features,
          platform,
          tone,
          language: hashtagLanguage,
          customPrompt: customNotes,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const tags = (data.hashtags || []).map((h: string) => (h.startsWith('#') ? h : `#${h}`));
      setHashtags(tags);
      toast.success('Hashtags regenerated');
    } catch (e: any) {
      toast.error(e.message || 'Hashtag generation failed');
    } finally {
      setGenTagLoading(false);
    }
  };

  const fullText = () => `${caption}\n\n${hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}`.trim();

  const downloadImage = async () => {
    if (!generatedImage) return;
    const a = document.createElement('a');
    a.href = generatedImage;
    a.download = `aureum-${product?.key.toLowerCase()}-${platform}.png`;
    a.click();
    toast.success('Downloaded');
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(fullText());
    toast.success('Caption copied to clipboard');
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(fullText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };
  const shareEmail = () => {
    const subj = encodeURIComponent(`${product?.name} — ${product?.tagline}`);
    const body = encodeURIComponent(fullText());
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };
  const shareFacebook = () => {
    const url = encodeURIComponent('https://www.aureum7trading.com');
    const quote = encodeURIComponent(fullText());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank', 'width=600,height=600');
  };
  const shareLinkedIn = () => {
    const url = encodeURIComponent('https://www.aureum7trading.com');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=600');
    navigator.clipboard.writeText(fullText()).then(() => toast.info('Caption copied — paste it into LinkedIn'));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" /> AI Social Template Studio
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Pick a product (or upload your own image), generate a branded template + caption with AI,
            then share to social media in one click.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Product</Label>
              <Select value={productKey} onValueChange={handleProductPick}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_OPTIONS.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Platform</Label>
              <Select value={platform} onValueChange={(v) => setPlatform(v as Platform)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram (1:1)</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['English', 'Spanish', 'French', 'German', 'Arabic', 'Swedish'].map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tone</Label>
              <Input value={tone} onChange={(e) => setTone(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Custom notes (optional)</Label>
            <Textarea
              rows={2}
              placeholder="e.g. promote 14-day free returns, target hotel buyers, autumn campaign..."
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
            />
          </div>

          <Tabs defaultValue="library">
            <TabsList>
              <TabsTrigger value="library"><ImageIcon className="h-4 w-4 mr-2" />Product library</TabsTrigger>
              <TabsTrigger value="upload"><Upload className="h-4 w-4 mr-2" />Upload my own</TabsTrigger>
            </TabsList>
            <TabsContent value="library" className="pt-3">
              <p className="text-sm text-muted-foreground">Using selected product's main image.</p>
            </TabsContent>
            <TabsContent value="upload" className="pt-3">
              <Input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} />
            </TabsContent>
          </Tabs>

          {sourceImage && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Source image</Label>
                <img loading="lazy" decoding="async" src={sourceImage} alt="Source product photo uploaded for the social template" className="rounded-md border w-full max-h-80 object-contain bg-muted" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Generated template</Label>
                  {generatedImage && (
                    <Button size="sm" variant="ghost" onClick={generateImage} disabled={genImgLoading}>
                      {genImgLoading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                      Regenerate
                    </Button>
                  )}
                </div>
                <div className="rounded-md border w-full min-h-40 max-h-80 flex items-center justify-center bg-muted overflow-hidden">
                  {generatedImage ? (
                    <img loading="lazy" decoding="async" src={generatedImage} alt="Generated branded social media template preview" className="w-full max-h-80 object-contain" />
                  ) : (
                    <span className="text-sm text-muted-foreground p-6 text-center">
                      Click "Generate Template" to compose a branded image.
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={generateImage} disabled={genImgLoading || !sourceImage}>
              {genImgLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Generate Template Image
            </Button>
            <Button onClick={generateCaption} variant="outline" disabled={genCapLoading}>
              {genCapLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
              Generate Caption + Hashtags
            </Button>
          </div>

          {(caption || hashtags.length > 0) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Caption</Label>
                <Button size="sm" variant="ghost" onClick={generateCaption} disabled={genCapLoading}>
                  {genCapLoading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                  Regenerate
                </Button>
              </div>
              <Textarea rows={6} value={caption} onChange={(e) => setCaption(e.target.value)} />
              <div className="flex items-center justify-between pt-2 gap-2 flex-wrap">
                <Label>Hashtags</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={hashtagLanguage} onValueChange={setHashtagLanguage}>
                    <SelectTrigger className="h-8 w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={generateHashtags} disabled={genTagLoading}>
                    {genTagLoading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                    Regenerate
                  </Button>
                  <Button size="sm" variant="ghost" onClick={saveCurrentSet} disabled={!hashtags.length}>
                    <Save className="mr-2 h-3 w-3" />Save set
                  </Button>
                </div>
              </div>
              <Input
                value={hashtags.join(' ')}
                onChange={(e) => setHashtags(e.target.value.split(/\s+/).filter(Boolean))}
              />
              {currentSets.length > 0 && (
                <div className="space-y-1 pt-2">
                  <Label className="text-xs text-muted-foreground">Saved sets for {product?.name}</Label>
                  <div className="flex flex-wrap gap-2">
                    {currentSets.map((s) => (
                      <div key={s.id} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs bg-muted/40">
                        <button className="hover:underline" onClick={() => loadSet(s.id)} title={s.tags.join(' ')}>
                          {s.name} <span className="text-muted-foreground">({s.language}, {s.tags.length})</span>
                        </button>
                        <button onClick={() => deleteSet(s.id)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>One-click share</CardTitle>
          <p className="text-sm text-muted-foreground">
            Image and caption are saved locally. Instagram requires posting from the app — download the image then paste
            the caption (auto-copied for LinkedIn / Instagram flow).
          </p>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadImage} disabled={!generatedImage}>
            <Download className="mr-2 h-4 w-4" /> Download image
          </Button>
          <Button variant="outline" onClick={copyText} disabled={!caption}>
            <Copy className="mr-2 h-4 w-4" /> Copy caption
          </Button>
          <Button variant="outline" onClick={shareWhatsApp} disabled={!caption}>
            <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
          </Button>
          <Button variant="outline" onClick={shareEmail} disabled={!caption}>
            <Mail className="mr-2 h-4 w-4" /> Email
          </Button>
          <Button variant="outline" onClick={shareFacebook} disabled={!caption}>
            <Facebook className="mr-2 h-4 w-4" /> Facebook
          </Button>
          <Button variant="outline" onClick={shareLinkedIn} disabled={!caption}>
            <Linkedin className="mr-2 h-4 w-4" /> LinkedIn
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
